/* Clerk <-> Treasurer link (TICKET-2778), shared by the import picker, the export
 * screens and the Clerk System status tab.
 *
 * The backend talks to ReGov (the Clerk system); the browser only ever talks to the
 * backend and never sees the county's ReGov API key. A county without the link
 * configured gets `{ configured: false }` and every screen looks exactly as before.
 */

export const REGOV_PATH_PREFIX = 'regov:';

export const isClerkBatchPath = (name) => typeof name === 'string' && name.startsWith(REGOV_PATH_PREFIX);

export const clerkBatchIdFromPath = (name) => (isClerkBatchPath(name)
  ? name.slice(REGOV_PATH_PREFIX.length)
  : undefined);

/* A listing entry as the picker shows it. SFTP entries are `{ name }`; Clerk batches
   also carry a `label` ("Clerk system - EXPORT2027.CSV (released ...)") and the real
   `fileName`. */
export const toPickerEntry = (file = {}) => ({
  value: file.name,
  label: file.label || file.name,
  fileName: file.fileName || file.name,
  isClerkBatch: isClerkBatchPath(file.name),
});

/* Only the "Export to clerk system" profiles (file popro.Y<FY>) can be sent. */
export const isClerkExportProfile = (fileName) => /^popro\./i.test(fileName || '');

/* One id per send, reused when the user presses Retry, so the Clerk system can never
   end up with the same file twice. */
export const newClientRef = () => {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `tl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const clerkSendSuccessMessage = (data = {}, what = 'File') => {
  const rows = data.rowCount != null ? ` (${data.rowCount} rows)` : '';
  if (data.duplicate) {
    return `${what} was already in the Clerk system's inbox${rows}; it was not sent twice.`;
  }
  return `${what} sent to the Clerk system${rows}. It is waiting for the clerk to import it.`;
};

export const clerkSendErrorMessage = (body = {}) => {
  const reason = (body && body.error) || 'the Clerk system did not answer';
  return `Not sent to the Clerk system: ${reason}`;
};

/* What to tell the user after an import that came from a Clerk batch. */
export const clerkImportMessage = (treasurerLink) => {
  if (!treasurerLink) return undefined;
  if (treasurerLink.acked || treasurerLink.alreadyImported) {
    return { ok: true, text: 'The Clerk system was told this batch has been imported.' };
  }
  return {
    ok: false,
    text: treasurerLink.error
      ? `Imported, but the Clerk system was not updated: ${treasurerLink.error}`
      : 'Imported, but the Clerk system could not be reached to mark the batch imported. It will be retried automatically; the batch will not be offered again.',
  };
};

export const INBOX_STATUS_COLORS = {
  pending: 'orange',
  imported: 'green',
  rejected: 'red',
};

export const RECEIVED_STATUS_TEXT = {
  acked: 'Imported (clerk notified)',
  imported: 'Imported (clerk not notified yet)',
};

export default {
  REGOV_PATH_PREFIX,
  isClerkBatchPath,
  clerkBatchIdFromPath,
  toPickerEntry,
  isClerkExportProfile,
  newClientRef,
  clerkSendSuccessMessage,
  clerkSendErrorMessage,
  clerkImportMessage,
};
